// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/TradeType.yaml
// Generated: 2026-07-08T08:07:38.270Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { tradeTypeService } from '@/services/core/TradeTypeService';
import { CreateTradeType, UpdateTradeType, TradeType } from '@/types/core/TradeType';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for TradeType
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const tradetypeKeys = {
  all: (tenantId?: string | null) => ['tradetypes', tenantId] as const,
  lists: (tenantId?: string | null) => [...tradetypeKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...tradetypeKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...tradetypeKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...tradetypeKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all TradeTypes
 */
export function useTradeTypes(options?: Omit<UseQueryOptions<TradeType[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<TradeType[], Error>({
    queryKey: tradetypeKeys.lists(tenantId),
    queryFn: () => tradeTypeService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single TradeType by ID
 */
export function useTradeType(
  id: string,
  options?: Omit<UseQueryOptions<TradeType | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<TradeType | undefined, Error>({
    queryKey: tradetypeKeys.detail(id, tenantId),
    queryFn: () => tradeTypeService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new TradeType
 */
export function useCreateTradeType() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<TradeType, Error, CreateTradeType, { previousTradeTypes?: TradeType[] }>({
    mutationFn: (data: CreateTradeType) => tradeTypeService.create(data),
    onMutate: async (newTradeType) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: tradetypeKeys.lists(tenantId) });

      // Snapshot previous value
      const previousTradeTypes = queryClient.getQueryData<TradeType[]>(tradetypeKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<TradeType[]>(tradetypeKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newTradeType, id: (newTradeType as { id?: string }).id ?? 'temp-' + Date.now() } as TradeType
      ]);

      return { previousTradeTypes };
    },
    onError: (_err, _newTradeType, context) => {
      // Rollback on error
      if (context?.previousTradeTypes) {
        queryClient.setQueryData(tradetypeKeys.lists(tenantId), context.previousTradeTypes);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: tradetypeKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing TradeType
 */
export function useUpdateTradeType() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<TradeType, Error, { id: string; data: UpdateTradeType }, { previousTradeType?: TradeType; previousTradeTypes?: TradeType[] }>({
    mutationFn: ({ id, data }) => tradeTypeService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: tradetypeKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: tradetypeKeys.lists(tenantId) });

      // Snapshot previous values
      const previousTradeType = queryClient.getQueryData<TradeType>(tradetypeKeys.detail(id, tenantId));
      const previousTradeTypes = queryClient.getQueryData<TradeType[]>(tradetypeKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<TradeType>(tradetypeKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as TradeType);

      // Optimistically update list
      queryClient.setQueryData<TradeType[]>(tradetypeKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as TradeType : item))
      );

      return { previousTradeType, previousTradeTypes };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousTradeType) {
        queryClient.setQueryData(tradetypeKeys.detail(id, tenantId), context.previousTradeType);
      }
      if (context?.previousTradeTypes) {
        queryClient.setQueryData(tradetypeKeys.lists(tenantId), context.previousTradeTypes);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: tradetypeKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: tradetypeKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a TradeType
 */
export function useDeleteTradeType() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousTradeTypes?: TradeType[] }>({
    mutationFn: (id: string) => tradeTypeService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: tradetypeKeys.lists(tenantId) });

      // Snapshot previous value
      const previousTradeTypes = queryClient.getQueryData<TradeType[]>(tradetypeKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<TradeType[]>(tradetypeKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousTradeTypes };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousTradeTypes) {
        queryClient.setQueryData(tradetypeKeys.lists(tenantId), context.previousTradeTypes);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: tradetypeKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSaveTradeType() {
  const createMutation = useCreateTradeType();
  const updateMutation = useUpdateTradeType();

  return {
    mutate: (params: { id?: string; data: CreateTradeType | UpdateTradeType }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdateTradeType }, options);
      } else {
        return createMutation.mutate(params.data as CreateTradeType, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
